/**
 * Conversation thread — `GET messaging/conversations/{id}/`, the full
 * ordered message array (oldest first). Polls every 8s via `refetchInterval`
 * — this backend has no websockets, plain REST polling matches current
 * Flutter behaviour. Compose box calls `send_message`, the bespoke
 * `{text, image}` action, not the generic conversation write schema.
 * `sendMessageImage` is the multipart variant, used whenever a file is
 * attached; text-only sends still go through the plain JSON `sendMessage`.
 */
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { getConversation, sendMessage, sendMessageImage } from '../../api/endpoints/messaging';
import { fullName } from '../../types/user';

const POLL_INTERVAL_MS = 8_000;

export function ConversationDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const conversationId = Number(id);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [text, setText] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imageInputKey, setImageInputKey] = useState(0);

  const query = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => getConversation(conversationId),
    enabled: Number.isFinite(conversationId),
    refetchInterval: POLL_INTERVAL_MS,
  });

  const send = useMutation({
    mutationFn: () =>
      selectedImageFile !== null
        ? sendMessageImage(conversationId, { text, image: selectedImageFile })
        : sendMessage(conversationId, { text }),
    onSuccess: () => {
      setText('');
      setSendError(null);
      setSelectedImageFile(null);
      setImageInputKey((key) => key + 1);
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (caught) => setSendError(parseApiError(caught).message),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setSendError(null);
    if (text === '' && selectedImageFile === null) return;
    send.mutate();
  }

  if (!Number.isFinite(conversationId)) {
    return <p>Invalid conversation id.</p>;
  }

  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const conversation = query.data;

  return (
    <section>
      <h2>
        Conversation with{' '}
        {conversation.participants_details
          .filter((participant) => participant.id !== user?.id)
          .map((participant) => fullName(participant))
          .join(', ') || 'yourself'}
      </h2>

      {conversation.messages.length === 0 && <p>No messages yet.</p>}
      {conversation.messages.length > 0 && (
        <ul>
          {conversation.messages.map((message) => (
            <li key={message.id}>
              <strong>{fullName(message.sender_details)}</strong> ({message.created_at}):{' '}
              {message.text !== '' && message.text}
              {message.image !== null && (
                <>
                  <br />
                  <img
                    src={message.image}
                    alt=""
                    loading="lazy"
                    className="message-image"
                  />
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {sendError !== null && (
          <p role="alert" className="form__error">
            {sendError}
          </p>
        )}

        <label htmlFor="message-text">Message</label>
        <textarea id="message-text" value={text} onChange={(event) => setText(event.target.value)} />

        <label htmlFor="message-image-upload">Attach image</label>{' '}
        <input
          key={imageInputKey}
          id="message-image-upload"
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={(event) => setSelectedImageFile(event.target.files?.[0] ?? null)}
        />

        <button type="submit" disabled={send.isPending || (text === '' && selectedImageFile === null)}>
          {send.isPending ? 'Sending…' : 'Send'}
        </button>
      </form>
    </section>
  );
}

export default ConversationDetailPage;
